import {Component, signal, OnInit, inject, output, input, computed} from '@angular/core';
import {FormGroup, FormControl, Validators} from '@angular/forms';
import {dateApresAujourdhui, trajetValidation} from '../validateurs/trajet.validateurs';
import {TrajetDto} from '../../../../shared/dto/trajet.dto';
import {ParametrageService} from '../../../../shared/services/parametrage.service';
import {isNotNullOrUndefined, isNullOrUndefined} from '@sigale-angular/src/utils/functions';

@Component({
	selector: 'app-formulaire-trajet',
	templateUrl: './formulaire-trajet.component.html',
	styleUrl: './formulaire-trajet.component.scss'
})
export class FormulaireTrajetComponent implements OnInit {
	// Injections
	private parametrageService = inject(ParametrageService);

	// Inputs
	trajet = input<TrajetDto | null>(null);

	// Outputs
	fermerFormulaireEvent = output<void>();
	submitFormulaireEvent = output<TrajetDto>();

	// Signaux
	formSoumis = signal(false);
	iconeBoutonSubmit = signal('');
	labelBoutonSubmit = signal('');
	titreFormulaire = signal('');

	// Variables
	aujourdhui: Date;
	formTrajet: FormGroup;

	// Computed
	sontChampsPrincipauxModifiables = computed(() => isNullOrUndefined(this.trajet()) || this.trajet()?.nombreReservations === 0);

	ngOnInit() {
		this.aujourdhui = new Date();
		this.initialiserModeFormulaire();
		this.initialiserFormulaire();
	}

	/**
	 * Initialise le formulaire et ses champs (valeurs par défaut et s'ils sont modifiables ou non).
	 */
	initialiserFormulaire() {
		const modifiable = this.sontChampsPrincipauxModifiables();
		const trajet = this.trajet();
		const minimumNombrePlaces = modifiable ? 1 : (trajet?.nombrePlaces ?? 1);

		this.formTrajet = new FormGroup({
			lieuDepart: new FormControl(
				{ value: trajet?.nomLieuDepart, disabled: !modifiable },
				Validators.required
			),
			adresseDepart: new FormControl(trajet?.adresseDepart),
			lieuArrivee: new FormControl(
				{ value: trajet?.nomLieuArrivee, disabled: !modifiable },
				Validators.required
			),
			adresseArrivee: new FormControl(trajet?.adresseArrivee),
			dateDepart: new FormControl(
				{ value: trajet ? new Date(trajet.dateDepart) : null, disabled: !modifiable },
				[Validators.required, dateApresAujourdhui()]
			),
			dateArrivee: new FormControl(
				{ value: trajet ? new Date(trajet.dateArrivee) : null, disabled: !modifiable },
				[Validators.required, dateApresAujourdhui()]
			),
			nombrePlaces: new FormControl(
				trajet?.nombrePlaces,
				[Validators.required, Validators.min(minimumNombrePlaces), Validators.max(100)]
			),
			informations: new FormControl(trajet?.information)
		}, { validators: trajetValidation() });

		if (isNullOrUndefined(this.trajet())) {
			this.recupererNombrePlacesDefaut();
		}
	}

	/**
	 * Initialise le titre et le bouton du formulaire selon le mode (création ou modification).
	 */
	initialiserModeFormulaire() {
		if (isNotNullOrUndefined(this.trajet())) {
			this.titreFormulaire.set('GESTION_NAVETTE.TRAJETS.TITRE_MODIFICATION_TRAJET');
			this.iconeBoutonSubmit.set('pi pi-save');
			this.labelBoutonSubmit.set('GLOBAL.BTN_ENREGISTRER');
		}
		else {
			this.iconeBoutonSubmit.set('pi pi-plus-circle');
			this.labelBoutonSubmit.set('GLOBAL.BTN_CREER');
			this.titreFormulaire.set('GESTION_NAVETTE.TRAJETS.TITRE_CREATION_TRAJET');
		}
	}

	/**
	 * Récupère le nombre de places par défaut pour un trajet en navette et l'affecte au champ du formulaire.
	 */
	recupererNombrePlacesDefaut(): void {
		this.parametrageService.getNombrePlacesDefautNavette().subscribe({
			next: (nombrePlacesDefaut) => {
				this.formTrajet.get('nombrePlaces')?.setValue(nombrePlacesDefaut);
			}
		});
	}

	/**
	 * Soumet le formulaire de trajet. Si le formulaire est valide, crée un objet TrajetDto avec les valeurs du formulaire et émet l'événement onSubmitFormulaire avec cet objet.
	 */
	submitFormulaire(): void {
		this.formSoumis.set(true);
		if (this.formTrajet.valid) {
			const formValue = this.formTrajet.getRawValue();
			const trajetDto = new TrajetDto({
				nombrePlaces: Number(formValue.nombrePlaces),
				nomLieuDepart: formValue.lieuDepart,
				adresseDepart: formValue.adresseDepart,
				dateDepart: formValue.dateDepart.getTime(),
				nomLieuArrivee: formValue.lieuArrivee,
				adresseArrivee: formValue.adresseArrivee,
				dateArrivee: formValue.dateArrivee.getTime(),
				information: formValue.informations
			});
			this.submitFormulaireEvent.emit(trajetDto);
		}
	}

	/**
	 * Retourne la clé de traduction de l'erreur pour un champ donné, ou null si aucune erreur.
	 *
	 * @param nomChamp le nom du contrôle dans le formulaire
	 */
	getMessageErreurChamp(nomChamp: string): string | null {
		const control = this.formTrajet.get(nomChamp);
		if (!control || !this.formSoumis() || !control.invalid) {
			return null;
		}

		const errorKey = Object.keys(control.errors ?? {})[0];
		switch (errorKey) {
			case 'dateInvalide':
				return 'GESTION_NAVETTE.FORMULAIRE.ERREUR.DATE_HEURE_INVALIDE';
			case 'max':
				return 'GESTION_NAVETTE.FORMULAIRE.ERREUR.NOMBRE_PLACES_MAX';
			case 'min':
				return 'GESTION_NAVETTE.FORMULAIRE.ERREUR.NOMBRE_PLACES_MIN';
			case 'required':
				return 'GLOBAL.ERREUR_CHAMP_OBLIGATOIRE';
		}
		return null;
	}

	/**
	 * Retourne la clé de traduction de l'erreur de lieux identiques, ou null si aucune erreur.
	 */
	getMessageErreurLieux(): string | null {
		if (this.formSoumis() && this.formTrajet.hasError('lieuxIdentiques')) {
			return 'GESTION_NAVETTE.FORMULAIRE.ERREUR.LIEUX_IDENTIQUES';
		}
		return null;
	}

	/**
	 * Retourne la clé de traduction de l'erreur de dates invalides, ou null si aucune erreur.
	 */
	getMessageErreurDates(): string | null {
		if (this.formSoumis() && this.formTrajet.hasError('arriveeAvantDepart')) {
			return 'GESTION_NAVETTE.FORMULAIRE.ERREUR.ARRIVEE_AVANT_DEPART';
		}
		return null;
	}

	/**
	 * Ferme le formulaire en émettant l'événement onFermer.
	 */
	fermerFormulaire(): void {
		this.fermerFormulaireEvent.emit();
	}
}
